/**
 * JavaScript-powered background patterns registry.
 *
 * This module exposes a registry keyed by pattern key (kebab-case, matching
 * the value stored in the backend `Pattern.pattern` field).
 *
 * Each pattern file in this folder must default-export a React component and
 * export a named `key` constant (kebab-case). The `key` is the source of
 * truth for the registry slot — adding a pattern means: (1) drop the file
 * here, (2) export a unique `key`, (3) add one import line below.
 *
 * The component receives the following props at render time:
 *
 *   {
 *     colors:     string[],   // Up to 4 hex colors (already normalized).
 *     brightness: number,     // 0..200 (percent).  Default 100.
 *     opacity:    number,     // 0..100 (percent).  Default 100.
 *     className:  string,     // Optional extra classes (positioning helpers).
 *     style:      object,     // Inline style merge — already includes
 *                             // --color-1..4 CSS custom properties so JS
 *                             // patterns can stay consistent with CSS ones.
 *   }
 *
 * The component must render an element that fills its parent (typically a
 * positioned <canvas>, <svg> or <div> with `position: absolute; inset: 0`).
 *
 * The wrapping pattern <div> applied by the renderer (with the pattern's
 * className, --color vars, brightness/opacity utilities) is still mounted —
 * the JS component is rendered as its child. This means JS patterns can
 * ALSO ship a CSS file (for a static fallback color or shell) by adding
 * `@import "./patterns/<key>.css"` to `renderer/patterns.css` like normal
 * patterns do. The CSS shell renders before the JS canvas mounts, which
 * keeps SSR/early-paint visuals reasonable.
 *
 * --- Why explicit imports? ---
 *
 * We previously used webpack's `require.context` to auto-discover modules.
 * That worked at runtime in dev/prod but is a webpack-only API: it threw
 * `require.context is not a function` under Jest (CRA's test runner uses
 * babel-jest, not webpack). Explicit imports remove the bundler dependency,
 * keep tree-shaking trivial, and make it obvious — at code review time —
 * which patterns ship in the bundle. The (small) cost is one import line
 * per new pattern; the corresponding `register` call below will fail at
 * module-evaluation time (during dev hot-reload) if you forget the `key`
 * export, which is exactly the kind of loud failure we want.
 */

import * as atomOrbits from "./atom_orbits";
import * as auroraColorfulLights from "./aurora_colorful_lights";
import * as auroraLights from "./aurora_lights";
import * as auroraRibbons from "./aurora_ribbons";
import * as boidsFlock from "./boids_flock";
import * as bokehDrift from "./bokeh_drift";
import * as cometTrails from "./comet_trails";
import * as constellationMap from "./constellation_map";
import * as cubeTumble from "./cube_tumble";
import * as dnaHelix from "./dna_helix";
import * as diagonalFrameFade from "./diagonal_frame_fade";
import * as doublePendulum from "./double_pendulum";
import * as dotGlobe from "./dot_globe";
import * as dotGlobeCorner from "./dot_globe_corner";
import * as dotGlobeCornerLeft from "./dot_globe_corner_left";
import * as equalizerBars from "./equalizer_bars";
import * as fallingPetals from "./falling_petals";
import * as fireflies from "./fireflies";
import * as fireworks from "./fireworks";
import * as flowField from "./flow_field";
import * as fourierSeries from "./fourier_series";
import * as galaxySpiral from "./galaxy_spiral";
import * as gameOfLife from "./game_of_life";
import * as hexPulse from "./hex_pulse";
import * as lavaLamp from "./lava_lamp";
import * as lightningStorm from "./lightning_storm";
import * as liquidBlobs from "./liquid_blobs";
import * as lorenzAttractor from "./lorenz_attractor";
import * as mandalaBloom from "./mandala_bloom";
import * as martingaleWalks from "./martingale_walks";
import * as matrixRain from "./matrix_rain";
import * as particleNetwork from "./particle_network";
import * as phyllotaxis from "./phyllotaxis";
import * as plasmaWaves from "./plasma_waves";
import * as rainOnGlass from "./rain_on_glass";
import * as ripplePool from "./ripple_pool";
import * as rotatingHalfCircleLines from "./rotating_half_circle_lines";
import * as snowfall from "./snowfall";
import * as soapBubbles from "./soap_bubbles";
import * as spirograph from "./spirograph";
import * as starfieldWarp from "./starfield_warp";
import * as synthwaveGrid from "./synthwave_grid";
import * as tessellationWave from "./tessellation_wave";
import * as voronoiCells from "./voronoi_cells";
import * as waveInterference from "./wave_interference";
import * as wireframeTerrain from "./wireframe_terrain";

// The full set of pattern modules. Order is irrelevant — the registry is
// keyed by each module's `key` export (and falls back to a snake→kebab
// transform of the import binding if `key` is missing, but every pattern
// is currently expected to provide one explicitly).
const PATTERN_MODULES = [
  atomOrbits,
  auroraColorfulLights,
  auroraLights,
  auroraRibbons,
  boidsFlock,
  bokehDrift,
  cometTrails,
  constellationMap,
  cubeTumble,
  dnaHelix,
  diagonalFrameFade,
  doublePendulum,
  dotGlobe,
  dotGlobeCorner,
  dotGlobeCornerLeft,
  equalizerBars,
  fallingPetals,
  fireflies,
  fireworks,
  flowField,
  fourierSeries,
  galaxySpiral,
  gameOfLife,
  hexPulse,
  lavaLamp,
  lightningStorm,
  liquidBlobs,
  lorenzAttractor,
  mandalaBloom,
  martingaleWalks,
  matrixRain,
  particleNetwork,
  phyllotaxis,
  plasmaWaves,
  rainOnGlass,
  ripplePool,
  rotatingHalfCircleLines,
  snowfall,
  soapBubbles,
  spirograph,
  starfieldWarp,
  synthwaveGrid,
  tessellationWave,
  voronoiCells,
  waveInterference,
  wireframeTerrain,
];

const registry = {};

PATTERN_MODULES.forEach((mod) => {
  if (!mod || !mod.default || !mod.key) return;
  registry[mod.key] = mod.default;
});

export const JS_PATTERN_REGISTRY = registry;

export const isJsPattern = (patternKey) =>
  Boolean(patternKey) && Object.prototype.hasOwnProperty.call(registry, patternKey);

export const getJsPatternComponent = (patternKey) =>
  patternKey ? registry[patternKey] || null : null;

export const listJsPatternKeys = () => Object.keys(registry);
